import React, { useRef, useContext, useEffect, useState } from "react";
import GoogleStreetView from "react-google-streetview";
import { useSelector } from "react-redux";
import { toLonLat, fromLonLat } from "ol/proj";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Point } from "ol/geom";
import { Feature } from "ol";
import { Modify } from "ol/interaction";
import { Icon, Style } from "ol/style";
import DirectionIcon from "../../icons/street-view-icon.png";
import MapContext from "../MapContext";
import MapMenu from "./MapMenu";

export const Google = ({
    startingPoint,
    projection,
    setActiveMaps,
    activeMaps,
    activeWindows,
    setActiveWindows,
}) => {
    const { map } = useContext(MapContext);
    const markerRef = useRef();
    const [googleLocation, setGoogleLocation] = useState();
    const [rotation, setRotation] = useState(0);
    const [olLocation, setOlLocation] = useState();
    const extent = useSelector((state) => state.configData.mapConfig.map.fullExtent);

    // handlers for google
    //    position
    const handlePositionChanged = (e) => {
        setGoogleLocation([e.lng(), e.lat()]);
    };
    //    rotation
    const handlePovChanged = (e) => {
        setRotation(e.heading);
    };

    const centerMap = () => {
        const newCenter = [
            ...markerRef.current.getSource().getFeatures()[0].getGeometry()
                .flatCoordinates,
        ];
        map.getView().animate({ center: newCenter });
    };

    // add marker, interaction and listener to map
    useEffect(() => {
        // set the starting point for google maps
        setOlLocation(toLonLat(startingPoint, projection));

        //Add marker and interaction
        const feature = new Feature({
            geometry: new Point(startingPoint),
            name: "Street-View Marker",
        });
        feature.setStyle(iconStyle);
        const source = new VectorSource({
            features: [feature],
        });
        markerRef.current = new VectorLayer({
            source: source,
            zIndex: 1000,
        });
        const modify = new Modify({
            source: source,
        });
        map.addLayer(markerRef.current);
        map.addInteraction(modify);

        //Add listener
        //  moves google street view when marker is dragged
        const handleModifyChange = (e) => {
            const olCoords = e.features.array_[0].getGeometry().getCoordinates();
            const coordsLonLat = toLonLat(olCoords, projection);

            setOlLocation(coordsLonLat);
        };
        modify.on("modifyend", handleModifyChange);
        return () => {
            map.removeInteraction(modify);
            map.removeLayer(markerRef.current);
            // makepath had this function, there might be an issue with webgl context in the future
            clearStreetviewContext();
            setRotation(0);
        };
    }, []);

    // handle rotation
    // triggered by event handler "handlePovChanged"
    useEffect(() => {
        if (rotation === undefined) return;
        const radians = degreesToRadians(rotation);
        const feature = markerRef.current.getSource().getFeatures()[0];

        // Update rotation for icon
        const icon = feature.getStyle().image_;
        icon.setRotation(radians);

        // Trigger change event to update map
        feature.changed();
    }, [rotation]);

    // Move marker when street view moves
    useEffect(() => {
        if (markerRef.current === undefined || googleLocation === undefined) return;
        const pointFeature = markerRef.current.getSource().getFeatures()[0].getGeometry();
        const reprojCenter = fromLonLat(googleLocation, projection);
        pointFeature.setCoordinates(reprojCenter);
    }, [googleLocation]);

    return olLocation === undefined ? (
        <div>Loading</div>
    ) : (
        <>
            <GoogleStreetView
                apiKey={process.env.REACT_APP_GOOGLE_KEY}
                streetViewPanoramaOptions={{
                    position: { lat: olLocation[1], lng: olLocation[0] },
                }}
                onPositionChanged={handlePositionChanged}
                onPovChanged={handlePovChanged}
            />
            <MapMenu
                mapName={"google"}
                setActiveMaps={setActiveMaps}
                activeMaps={activeMaps}
                setActiveWindows={setActiveWindows}
                activeWindows={activeWindows}
                centerMap={centerMap}
            />
        </>
    );
};

const iconStyle = new Style({
    image: new Icon({
        anchor: [0.5, 56],
        anchorXUnits: "fraction",
        anchorYUnits: "pixels",
        src: DirectionIcon,
        zIndex: 1000,
        scale: 0.6,
    }),
});

const degreesToRadians = (degree) => {
    const radians = degree * (Math.PI / 180);
    return radians;
};

export const clearStreetviewContext = () => {
    const el = document.querySelector(".mapsConsumerUiSceneInternalCoreScene__canvas");
    if (el) {
        const context = el.getContext("webgl");
        if (context) {
            const extension = context.getExtension("WEBGL_lose_context");
            if (extension) {
                extension.loseContext();
            }
        }
    }
};
